export interface Curation {
  id?: string
  curationType?: CurationType
  title?: LangStringYomi
  summary?: LangString
  rights?: LangString
  rightsType?: string
  tag?: TagField[]
  responsibility?: LangString
  image?: ImageSelection
  subPages?: string[]
  theme?: string
  workspace?: string
  workspaceTags?: string[]
  permission?: Permission
  edit?: Edit
  parts?: CurationPartDefinition[]
}

export interface CurationPartDefinition {
  cpid?: string
  type?: string
  styleClass?: string

  [others: string]: any
}

export interface PartAreaAnnotation extends CurationPartDefinition {
  rect?: Rect
  title?: LangString
  description?: LangString
  parts?: CurationPartDefinition[]
}

export interface PartIframe extends CurationPartDefinition {
  iframeSrc?: string
  caption?: LangString
  description?: LangString
  layout?: IframeLayout
}

export interface PartImage extends CurationPartDefinition {
  image?: ImageSelection
  imageEn?: ImageSelection
  height?: string
  caption?: LangString
  description?: LangString
  background?: ImageBackground
  layout?: ImageLayout
}

export interface PartImageAnnotation extends CurationPartDefinition {
  image?: ImageSelection
  initial?: PartAreaAnnotation
  parts?: PartAreaAnnotation[]
}

export interface PartIndex extends CurationPartDefinition {
  style?: IndexStyle
}

export interface PartList extends CurationPartDefinition {
  title?: LangString
  viewType?: ListViewType
  csId?: string
  columns?: CustomSearchColumn[]
  mapParam?: MapParam
  parts?: CurationPartDefinition[]
  openActionType?: OpenActionType
}

export interface PartListCuration extends CurationPartDefinition {
  id?: string
  description?: LangString
  note?: LangDelta
  latlon?: LatLon
  image?: ImageSelection
}

export interface PartListCustomSearch extends CurationPartDefinition {
  id?: string
  description?: LangString
  note?: LangDelta
  image?: ImageSelection
}

export interface PartListDatabase extends CurationPartDefinition {
  id?: string
  description?: LangString
  note?: LangDelta
  image?: ImageSelection
}

export interface PartListExternalLink extends CurationPartDefinition {
  title?: LangString
  url?: string
  source?: LangString
  description?: LangString
  note?: LangDelta
  image?: ImageSelection
  latlon?: LatLon
}

export interface PartListItem extends CurationPartDefinition {
  id?: string
  title?: LangString
  description?: LangString
  note?: LangDelta
  linkUrl?: string
  image?: ImageSelection
  latlon?: LatLon
}

export interface PartListOrganization extends CurationPartDefinition {
  id?: string
  description?: LangString
  note?: LangDelta
  image?: ImageSelection
  latlon?: LatLon
}

export interface PartListSearchResult extends CurationPartDefinition {
  dataType?: DataKey
  title?: LangString
  query?: SearchQuery
  csId?: string
}

export interface PartSearchBox extends CurationPartDefinition {
  csId?: string
  searchDef?: CustomSearchDefinition
  mode?: SearchBoxMode
  dataType?: DataKey
}

export interface PartSection extends CurationPartDefinition {
  title?: LangString
  icon?: string
  parts?: CurationPartDefinition[]
}

export interface PartSubPageList extends CurationPartDefinition {
}

export interface PartTab extends CurationPartDefinition {
  parts?: PartTabContent[]
}

export interface PartTabContent extends CurationPartDefinition {
  title?: LangString
  subTitle?: LangString
  color?: string
  width?: number
  parts?: CurationPartDefinition[]
}

export interface PartText extends CurationPartDefinition {
  ja?: Delta
  en?: Delta
}

export interface PartTimePoint extends CurationPartDefinition {
  time?: Time
  description?: LangString
  parts?: CurationPartDefinition[]
}

export interface PartTimeline extends CurationPartDefinition {
  title?: LangString
  parts?: PartTimePoint[]
  timeline?: Timeline
  layout?: TimeLineLayout
}

export interface Time {
  name?: LangString
  start?: number
  startFraction?: number
  end?: number
  endFraction?: number
  labelHeight?: number
}

export interface Timeline {
  start?: number
  end?: number
}

export interface TagField {
  id?: string
  label?: LangString
}

export interface MapParam {
  center?: LatLon
  zoom?: number
  mode?: MapMode
}

export type CurationType =
  'NOTE' |
  'GALLERY' |
  'SUBPAGE' |
  'DB' |
  'ORGANIZATION' |
  'PAGE' |
  'SYSTEM'
export const CURATION_TYPE_DEF: EnumDef = Object.freeze({
  id: 'jps-curation_type',
  c: {
    NOTE: {
      l: {
        ja: 'ノート',
        en: 'Note'
      },
      d: {}
    },
    GALLERY: {
      l: {
        ja: 'ギャラリー',
        en: 'Gallery'
      },
      d: {}
    },
    SUBPAGE: {
      l: {
        ja: 'サブページ',
        en: 'Subpage'
      },
      d: {}
    },
    DB: {
      l: {
        ja: 'データベースページ',
        en: 'Database Page'
      },
      d: {}
    },
    ORGANIZATION: {
      l: {
        ja: '組織ページ',
        en: 'Organization Page'
      },
      d: {}
    },
    PAGE: {
      l: {
        ja: '各種ページ',
        en: 'Page'
      },
      d: {}
    },
    SYSTEM: {
      l: {
        ja: 'システム用',
        en: 'For System'
      },
      d: {}
    }
  }
})
export type IframeLayout =
  'right_description' |
  'left_description' |
  'bottom_description' |
  'caption'
export const IFRAME_LAYOUT_DEF: EnumDef = Object.freeze({
  id: 'jps-iframe_layout',
  c: {
    right_description: {
      l: {
        ja: '右に説明',
        en: 'Right Description'
      },
      d: {}
    },
    left_description: {
      l: {
        ja: '左に説明',
        en: 'Left Description'
      },
      d: {}
    },
    bottom_description: {
      l: {
        ja: '下に説明',
        en: 'Bottom Description'
      },
      d: {}
    },
    caption: {
      l: {
        ja: 'キャプションのみ',
        en: 'Caption Only'
      },
      d: {}
    }
  }
})
export type ImageBackground =
  'none' |
  'grey' |
  'black'
export const IMAGE_BACKGROUND_DEF: EnumDef = Object.freeze({
  id: 'jps-image_background',
  c: {
    none: {
      l: {
        ja: '無し',
        en: 'None'
      },
      d: {}
    },
    grey: {
      l: {
        ja: '灰色',
        en: 'Grey'
      },
      d: {}
    },
    black: {
      l: {
        ja: '黒',
        en: 'Black'
      },
      d: {}
    }
  }
})
export type ImageLayout =
  'right_description' |
  'left_description' |
  'bottom_description' |
  'viewer' |
  'caption'
export const IMAGE_LAYOUT_DEF: EnumDef = Object.freeze({
  id: 'jps-image_layout',
  c: {
    right_description: {
      l: {
        ja: '右に説明',
        en: 'Right Description'
      },
      d: {}
    },
    left_description: {
      l: {
        ja: '左に説明',
        en: 'Left Description'
      },
      d: {}
    },
    bottom_description: {
      l: {
        ja: '下に説明',
        en: 'Bottom Description'
      },
      d: {}
    },
    viewer: {
      l: {
        ja: 'ビューア',
        en: 'Viewer'
      },
      d: {}
    },
    caption: {
      l: {
        ja: 'キャプションのみ',
        en: 'Caption Only'
      },
      d: {}
    }
  }
})
export type IndexStyle =
  'normal'
export const INDEX_STYLE_DEF: EnumDef = Object.freeze({
  id: 'jps-index_style',
  c: {
    normal: {
      l: {},
      d: {}
    }
  }
})
export type ListViewType =
  'bullet' |
  'card' |
  'tile' |
  'table' |
  'image' |
  'imagewide' |
  'description' |
  'reference' |
  'map' |
  'blind'
export const LIST_VIEW_TYPE_DEF: EnumDef = Object.freeze({
  id: 'jps-list_view_type',
  c: {
    bullet: {
      l: {
        ja: 'リスト',
        en: 'List'
      },
      d: {}
    },
    card: {
      l: {
        ja: 'スライド',
        en: 'Slides'
      },
      d: {}
    },
    tile: {
      l: {
        ja: 'タイル',
        en: 'Tiles'
      },
      d: {}
    },
    table: {
      l: {
        ja: '表',
        en: 'Table'
      },
      d: {}
    },
    image: {
      l: {
        ja: '画像+解説',
        en: 'Image+Description'
      },
      d: {}
    },
    imagewide: {
      l: {
        ja: '画像+解説（ワイド）',
        en: 'Image+Description Wide'
      },
      d: {}
    },
    description: {
      l: {
        ja: '説明リスト',
        en: 'Description List'
      },
      d: {}
    },
    reference: {
      l: {
        ja: '参考文献',
        en: 'References'
      },
      d: {}
    },
    map: {
      l: {
        ja: '地図',
        en: 'Map'
      },
      d: {}
    },
    blind: {
      l: {
        ja: '屏風',
        en: 'Folding Screen'
      },
      d: {}
    }
  }
})
export type MapMode =
  'NORMAL' |
  'MERGE'
export const MAP_MODE_DEF: EnumDef = Object.freeze({
  id: 'jps-map_mode',
  c: {
    NORMAL: {
      l: {
        ja: '通常',
        en: 'Normal'
      },
      d: {}
    },
    MERGE: {
      l: {
        ja: '集約',
        en: 'Merge'
      },
      d: {}
    }
  }
})
export type OpenActionType =
  'modal' |
  'link'
export const OPEN_ACTION_TYPE_DEF: EnumDef = Object.freeze({
  id: 'jps-open_action_type',
  c: {
    modal: {
      l: {
        ja: 'モーダル',
        en: 'Modal'
      },
      d: {
        ja: '詳細を表示するモーダルが開きます'
      }
    },
    link: {
      l: {
        ja: 'リンク',
        en: 'Link'
      },
      d: {
        ja: '直接リンク先に飛びます'
      }
    }
  }
})
export type DataKey =
  'item' |
  'curation' |
  'csearch' |
  'database' |
  'organization' |
  'project'
export const DATA_KEY_DEF: EnumDef = Object.freeze({
  id: 'jps-data_key',
  c: {
    item: {
      l: {},
      d: {}
    },
    curation: {
      l: {},
      d: {}
    },
    csearch: {
      l: {},
      d: {}
    },
    database: {
      l: {},
      d: {}
    },
    organization: {
      l: {},
      d: {}
    },
    project: {
      l: {},
      d: {}
    }
  }
})
export type SearchBoxMode =
  'NORMAL' |
  'NOHEADER' |
  'KEYWORD'
export const SEARCH_BOX_MODE_DEF: EnumDef = Object.freeze({
  id: 'jps-search_box_mode',
  c: {
    NORMAL: {
      l: {
        ja: '通常表示',
        en: 'Default'
      },
      d: {}
    },
    NOHEADER: {
      l: {
        ja: 'ヘッダーを非表示にします',
        en: 'Hide header'
      },
      d: {}
    },
    KEYWORD: {
      l: {
        ja: 'ヘッダー、詳細検索を非表示にします',
        en: 'Hide header and filters'
      },
      d: {}
    }
  }
})
export type TimeLineLayout =
  'bar' |
  'withyears' |
  'table'
export const TIME_LINE_LAYOUT_DEF: EnumDef = Object.freeze({
  id: 'jps-time_line_layout',
  c: {
    bar: {
      l: {
        ja: 'バー表示',
        en: 'Bar'
      },
      d: {}
    },
    withyears: {
      l: {
        ja: 'バー表示（開始・終了年付）',
        en: 'Bar with Start / End Year'
      },
      d: {}
    },
    table: {
      l: {
        ja: '表形式',
        en: 'Table'
      },
      d: {}
    }
  }
})
